import React, { useRef, useState } from 'react';
import { RiCloseLine } from '@remixicon/react';

/**
 * AmenitiesInput — controlled multi-select chip/tag input.
 *
 * Behaviour:
 *   - Comma or Enter converts the current text into a chip.
 *   - Paste "a, b, c" auto-splits into three chips.
 *   - Backspace on empty input removes the last chip.
 *   - Duplicates (case-insensitive trim) are silently rejected.
 *
 * Controlled props:
 *   @param {string[]} value        Current amenities list.
 *   @param {(v: string[]) => void} onChange
 *   @param {string}   [placeholder]
 *   @param {boolean}  [disabled]
 */
const AmenitiesInput = ({
  value = [],
  onChange,
  placeholder = 'Type an amenity and press Enter or comma...',
  disabled = false,
}) => {
  const [draft, setDraft] = useState('');
  const inputRef = useRef(null);

  const commit = (raw) => {
    // Split on commas so a paste like "WiFi, Pool, Gym" adds three chips.
    const pieces = String(raw ?? '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    if (pieces.length === 0) return;
    // Case-insensitive dedupe against the existing list + within the paste.
    const existingLower = new Set(value.map((v) => v.toLowerCase()));
    const additions = [];
    for (const p of pieces) {
      const lower = p.toLowerCase();
      if (existingLower.has(lower)) continue;
      existingLower.add(lower);
      additions.push(p);
    }
    if (additions.length > 0) onChange?.([...value, ...additions]);
  };

  const removeAt = (index) => {
    if (index < 0 || index >= value.length) return;
    onChange?.(value.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e) => {
    if (disabled) return;
    if (e.key === 'Enter' || e.key === ',') {
      // Enter/, only fires the commit when the draft has content — an empty
      // Enter shouldn't create a phantom chip.
      if (draft.trim().length > 0) {
        e.preventDefault();
        commit(draft);
        setDraft('');
      } else if (e.key === ',') {
        // Even with no draft, swallow the comma so it doesn't insert.
        e.preventDefault();
      }
      return;
    }
    if (e.key === 'Backspace' && draft.length === 0 && value.length > 0) {
      e.preventDefault();
      removeAt(value.length - 1);
    }
  };

  const handleChange = (e) => {
    const next = e.target.value;
    // If the user typed a comma inline (mid-word paste, autocomplete, etc.)
    // split it into chips + keep whatever's after the last comma as the draft.
    if (next.includes(',')) {
      const parts = next.split(',');
      const trailing = parts.pop() || '';
      commit(parts.join(','));
      setDraft(trailing);
    } else {
      setDraft(next);
    }
  };

  const handlePaste = (e) => {
    if (disabled) return;
    const pasted = e.clipboardData?.getData('text');
    if (pasted && pasted.includes(',')) {
      e.preventDefault();
      commit(pasted);
      setDraft('');
    }
  };

  const handleBlur = () => {
    if (draft.trim().length > 0) {
      commit(draft);
      setDraft('');
    }
  };

  return (
    <div
      onClick={() => inputRef.current?.focus()}
      className={`w-full min-h-[42px] px-2.5 py-1.5 border border-gray-200 rounded-xl bg-white flex flex-wrap items-center gap-1.5 focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-transparent ${
        disabled ? 'opacity-60 cursor-not-allowed bg-gray-50' : 'cursor-text'
      }`}
    >
      {value.map((chip, i) => (
        <span
          key={`${chip}-${i}`}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent text-primary-700 text-xs font-medium"
        >
          {chip}
          {!disabled && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                removeAt(i);
              }}
              className="hover:text-primary-900"
              aria-label={`Remove ${chip}`}
            >
              <RiCloseLine className="w-3.5 h-3.5" />
            </button>
          )}
        </span>
      ))}
      <input
        ref={inputRef}
        type="text"
        value={draft}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onBlur={handleBlur}
        placeholder={value.length === 0 ? placeholder : ''}
        disabled={disabled}
        className="flex-1 min-w-[120px] py-1 px-1 text-sm text-gray-900 placeholder-gray-400 bg-transparent focus:outline-none disabled:bg-transparent"
      />
    </div>
  );
};

export default AmenitiesInput;
